import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { AiIcon } from '../../../icons';
import { Tag } from '../../atoms/Tag';
import { Card } from '../Card';

type Props = {
  title: string;
  /** Origin badge shown in the header. */
  badgeLabel?: string;
  /** Optional control in the header (e.g. a button applying the recommendation). */
  action?: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
};

const DEFAULT_BADGE_LABEL = 'Synthèse IA';
const HEADER_TINT_OPACITY = 0.08;

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.alpha(theme.palette.ai.main, HEADER_TINT_OPACITY),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Title = styled('h3')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  margin: 0,
  flex: 1,
  minWidth: 0,
}));

const Body = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
}));

/**
 * Container for content produced by AI (summary, recommendation). The purple AI tint is reserved to it,
 * so users always know which statements come from the model.
 */
export function AiPanel(props: Props) {
  return (
    <Card data-testid={props['data-testid'] ?? 'ai-panel'}>
      <Header>
        <AiIcon fontSize="small" color="ai" />
        <Title>
          {props.title}
        </Title>
        <Tag color="ai" label={props.badgeLabel ?? DEFAULT_BADGE_LABEL} />
        {props.action}
      </Header>
      <Body>{props.children}</Body>
    </Card>
  );
}

export type { Props as AiPanelProps };
